package com.crowpay.views.dialogs

import com.crowpay.ChangeRequest
import com.crowpay.ChangeRequestItem
import com.crowpay.Client
import com.crowpay.Contractor
import com.crowpay.Credit
import com.crowpay.DisplayableState
import com.crowpay.FlowMessage
import com.crowpay.Formats
import com.crowpay.HasDisplayName
import com.crowpay.LineItem
import com.crowpay.LineItemInterface
import com.crowpay.PayAppItem
import com.crowpay.PayApplication
import com.crowpay.Project
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.extensions.isUnread
import com.crowpay.format
import com.crowpay.projectCreditGroup
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.JumpTo
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.nav.viewIsNarrow
import com.crowpay.views.components.nav.viewIsWide
import com.crowpay.views.components.project.activity.concerns.ConcernCardInfo
import com.crowpay.views.components.project.activity.concerns.MessageData
import com.crowpay.views.components.project.work.changeOrders.ChangeItemReadReceipts
import com.crowpay.views.components.project.work.changeOrders.status
import com.crowpay.views.components.project.work.payApps.individualStatus
import com.crowpay.views.components.project.work.payApps.overallStatus
import com.crowpay.views.components.project.work.payApps.status
import com.crowpay.views.components.space
import com.crowpay.views.components.unreadIconWhen
import com.crowpay.views.screens.common.DialogOrPage
import com.crowpay.views.screens.common.ProjectRouter
import com.crowpay.views.screens.common.ProjectView
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.BoldSemantic
import com.crowpay.views.theming.DisplayableStateSemantic
import com.crowpay.views.theming.FormHeader
import com.crowpay.views.theming.FormMessage
import com.crowpay.views.theming.SubtitleSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.boldSubTitle
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.outlined
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.specCenteredTextWithUnread
import com.crowpay.views.theming.subTitle
import com.crowpay.views.theming.xlTitle
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.dp
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.Page
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.mainScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.onRemove
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.atTopCenter
import com.lightningkite.kiteui.views.card
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.dialog
import com.lightningkite.kiteui.views.direct.RowOrCol
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.dismissBackground
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.padded
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.separator
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.text
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.now
import kotlinx.coroutines.launch
import kotlinx.datetime.Instant

abstract class BasicFlowForm<T>(
    val items: List<T>,
    val formTitle: String,
    val formState: DisplayableState,
    val created: Instant,
    val clientName: String,
    val contractorName: String
) : Page, DialogOrPage {
    private val ViewWriter.outerPadding get() = if (viewIsNarrow) null else AppDimensions.fullIndent
    private fun RowOrCol.outerPadding() { outerPadding?.let { space(it) } }

    private fun ViewWriter.closeForm() {
        dialogScreenNavigator.dismiss()
        if (screenNavigator === mainScreenNavigator) mainScreenNavigator.dismiss()
    }

    abstract fun RowOrCol.header()

    interface MessageWithState : FlowMessage {
        val state: DisplayableState
    }
    fun FlowMessage.withState(state: DisplayableState): MessageWithState =
        object : MessageWithState, FlowMessage by this {
            override val state: DisplayableState = state
        }

    abstract fun getMessage(item: T): MessageWithState

    abstract suspend fun markAsReadDelayUpdate(items: List<T>): (() -> Unit)?

    private fun ViewWriter.page() = col {
        spacing = 0.px
        row {
            spacing = 0.px
            expanding - compact - FormHeader.onNext - xlTitle {
                align = Align.Center
                content = formTitle
            }
            if (viewIsWide) sizeConstraints(width = 3.25.rem) - secondaryButton - button {
                centered - icon(Icon.close, "Close Form")
                onClick { closeForm() }
            }
        }

        row {
            spacing = 0.px
            outerPadding()
            sizeConstraints(height = 5.rem) - expanding - SubtitleSemantic.onNext - row {
                expanding - centered - DisplayableStateSemantic(formState).onNext - text(formState.displayName)
                centered - sizeConstraints(width = 7.rem) - compact - col {
                    spacing = 2.dp
                    text {
                        align = Align.Center
                        content = now().format(Formats.mmddyyyy)
                    }
                    text {
                        align = Align.Center
                        content = created.format(Formats.mmddyyyy)
                    }
                }
            }
            outerPadding()
        }

        separator()

        row {
            spacing = 0.px
            outerPadding()
            expanding - col {
                space(0.15)
                spacing = AppDimensions.majorColumnSpacing

                col {
                    spacing = 0.5.rem

                    header()

                    greySeparator()
                }

                col {
                    outlined - stack {
                        col {
                            spacing = 0.px
                            boldSubTitle("History")

                            col scope@{
                                space(0.0)  // don't remove until spacing is fixed in v5

                                launch {
                                    markAsReadDelayUpdate(items)?.also(this@scope::onRemove)
                                }

                                items
                                    .map(::getMessage)
                                    .sortedBy { it.created }
                                    .forEach { message ->
                                        FormMessage.onNext - col {
                                            val unread = shared {
                                                message.readBy.isUnread(notNullSession().userId)
                                            }
                                            spacing = 0.dp
                                            card - (DisplayableStateSemantic(message.state) + BoldSemantic).onNext - stack {
                                                row {
                                                    spacing = AppDimensions.expandButtonSpace
                                                    centered - unreadIconWhen { unread() }
                                                    body(message.state.displayName)
                                                }
                                            }

                                            padded - col {
                                                row {
                                                    centered - bodyBold(if (message.clientMessage) clientName else contractorName)
                                                    centered - smallBody(message.created.format(Formats.mmddyyyy_hmm))
                                                }

                                                body("(${message.type.displayName})    ${message.message}")
                                            }
                                        }
                                    }
                            }
                        }
                    }
                }

                space(0.15)
            }
            outerPadding()
        }
    }

    override fun ViewWriter.renderAsDialog(): ViewModifiable = dismissBackground {
        scrolls - col {
            spacing = AppDimensions.fullIndent
            space()
            sizeConstraints(width = AppDimensions.pageWidth) - atTopCenter - dialog - page()
            space(2.0)
        }
    }

    override fun ViewWriter.renderAsPage(): ViewModifiable = scrolls - page()
}

class DrawWaitedDetails(
    val payApp: PayApplication,
    val allItems: List<PayAppItem>,
    val lines: Map<UUID, LineItemInterface>,
    clientName: String,
    contractorName: String
) : BasicFlowForm<PayAppItem>(
    items = allItems.filter { it.denied != null },
    formTitle = "Draw Waited",
    formState = allItems.map { it.status }.toSet().overallStatus(),
    created = payApp.submitted ?: payApp.created,
    clientName = clientName,
    contractorName = contractorName
) {
    override fun RowOrCol.header() {
        val unread = shared {
            payApp.isUnread(notNullSession().userId)
        }
        bodyBold("Draw")
        row {
            expanding - row {
                expanding - centered - subTitle("Draw ${payApp.number}")
                centered - subTitle(allItems.sumOf { it.amount }.renderDollars())
            }
            centered - secondaryButton - button {
                specCenteredTextWithUnread("View Draw") { unread() }
                onClick("View Item") {
                    mainScreenNavigator.run {
                        if (stack.value.lastOrNull() !is ProjectView) navigate(ProjectRouter(payApp.project))
                    }
                    JumpTo.PayApp(payApp._id, animate = false)
                    dialogScreenNavigator.dismiss()
                }
            }
        }
    }

    override fun getMessage(item: PayAppItem): MessageWithState = MessageData(item, lines[item.lineItem] ?: notFoundError<LineItem>(), payApp.number).withState(item.individualStatus)

    override suspend fun markAsReadDelayUpdate(items: List<PayAppItem>): (() -> Unit)? =
        notNullSession().payAppItems.markFeedbackAsReadDelayUpdate(items)?.let { { println("updating"); it() } }
}

class ChangesRejectedDetails(
    val change: ChangeRequest,
    val allItems: List<ChangeRequestItem>,
    clientName: String,
    contractorName: String
) : BasicFlowForm<ChangeRequestItem>(
    items = allItems.filter { it.changeRejected != null },
    formTitle = "Change Rejected",
    formState = allItems.maxOf { it.status },
    created = change.created,
    clientName = clientName,
    contractorName = contractorName
) {
    override fun RowOrCol.header() {
        val unread = shared {
            change.isUnread(notNullSession().userId) || allItems.any { it.readBy.isUnread(notNullSession().userId) }
        }
        bodyBold("Change")
        row {
            expanding - row {
                expanding - centered - subTitle("Change ${change.number}")
                centered - subTitle(allItems.sumOf { it.priceChange ?: 0 }.renderDollars())
            }
            centered - secondaryButton - button {
                specCenteredTextWithUnread("View Change") { unread() }
                onClick("View Item") {
                    mainScreenNavigator.run {
                        if (stack.value.lastOrNull() !is ProjectView) navigate(ProjectRouter(change.project))
                    }
                    JumpTo.ChangeItem(items.first()._id, animate = false)
                    dialogScreenNavigator.dismiss()
                }
            }
        }
    }

    override fun getMessage(item: ChangeRequestItem): MessageWithState = MessageData(item).withState(item.status)

    override suspend fun markAsReadDelayUpdate(items: List<ChangeRequestItem>): (() -> Unit)? =
        ChangeItemReadReceipts.markFeedbackAsReadDelayUpdate(items)
}

class ProjectCreditDetails(
    val credit: Credit,
    val client: Client,
    val contractor: Contractor,
    val userId: UUID,
    val project: Project
) : BasicFlowForm<Credit>(
    items = listOf(credit),
    formTitle = "Project Credit",
    formState = issuedState,
    created = credit.timestamp,
    clientName = client.name,
    contractorName = contractor.name
) {
    override fun getMessage(item: Credit): MessageWithState = MessageData(
        message = item.reason,
        clientMessage = false,
        created = item.timestamp,
        sender = contractor._id,
        type = HasDisplayName.Manual("Project Credit, ${item.amount.renderDollars()}"),
        readBy = credit.readBy
    ).withState(issuedState)

    override suspend fun markAsReadDelayUpdate(items: List<Credit>): (() -> Unit)? =
        notNullSession().projects.markCreditsAsReadDelayUpdate(setOf(project), condition { it.projectCreditGroup eq credit.projectCreditGroup })

    override fun RowOrCol.header() {
        bodyBold("Credit")
        row {
            expanding - centered - subTitle("Credit ${credit.projectCreditGroup ?: 0}")
            centered - subTitle(credit.amount.renderDollars())
        }
    }

    companion object {
        val issuedState = ConcernCardInfo.ManualState("Issued", AppColors.secondary.main)
    }
}